import { EFinancingProgram, EMessageType, EPaymentFrequency, EStaticMerchantIds } from '@if/core/types'
import { getEnvRuntimeVariable, lazyLoad } from '@if/core/utils'
import type { Resources } from '@src/@types/resources'
import type { CreditApplication } from './CreditApplicationSchema'

export const SELECTED_COMPANY_LOCAL_STORAGE = 'selectedCompany'

export const MIN_RATE_CREDIT_RSLA_SILVERPLAN = 13.99

export const LOCK_CREDITAPPLICATION_ENTITYTYPE = 'creditapplication'

export type NormsMessageKey = keyof Resources['translation']['CreditDecisionNorms']

export const SUPPORTED_PAYMENT_FREQUENCIES = [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly]

export const CURRENT_USER_PARTICIPANT_TYPE = EMessageType.Internal

export const SUPPORTED_INSTANT_MESSAGE_PROGRAMS: EFinancingProgram[] = [
  EFinancingProgram.Auto,
  EFinancingProgram.IfXpress,
  EFinancingProgram.Products,
  EFinancingProgram.Personal,
  EFinancingProgram.MechanicalWarranty,
]

export enum EEntryType {
  Credit = 'credit',
  Funding = 'funding',
  DocumentsAndIncomes = 'documentsAndIncomes',
  Financing = 'financing',
  RushAndTag = 'rushAndTag',
}
export const getEEntryTypeList = lazyLoad(() => Object.values(EEntryType))

export enum ECreditTaskDashboardEntryAction {
  TaskAction = 'TaskAction',
  ConfirmAllIncomesAction = 'ConfirmAllIncomesAction',
  DocumentAction = 'DocumentAction',
  CreditDecisionAction = 'CreditDecisionAction',
  WorksheetDecisionAction = 'WorksheetDecisionAction',
  CreateLoanAction = 'CreateLoanAction',
  CreateWorksheetAction = 'CreateWorksheetAction',
}

export enum EAssignmentFilter {
  Blank = 'blank',
  AssignedApplication = '0',
  UnassignedApplication = '1',
  MyApplicationsOnly = '2',
  MyApplicationsAndUnassignedOnes = '3',
}

export const getEAssignmentFilterList = lazyLoad(() => Object.values(EAssignmentFilter))

export enum EAfterNormEffectsEnum {
  MerchantAgreementRate = 'merchantAgreementRate',
  IFinancePromotion = 'iFinancePromotion',
  MerchantRateModifier = 'merchantRateModifier',
  HighAmountFinanced = 'highAmountFinanced',
}

export enum EGapInsuranceDuration {
  lessThanSixtyMonths = 0,
  sixtyMonthsOrMore = 1,
  unknown = -1,
}

export enum EExtendedServiceDuration {
  twelveMonths = 12,
  twentyFourMonths = 24,
  thirtySixMonth = 36,
  unknown = 0,
}

export const getEExtendedServiceDurationList = lazyLoad(() =>
  Object.values(EExtendedServiceDuration).filter((x) => typeof x === 'number'),
)

export enum ETermMax {
  six = 6,
  twelve = 12,
  eighteen = 18,
  twentyFour = 24,
  thirty = 30,
  thirtySix = 36,
  fortyTwo = 42,
  fortyEight = 48,
  fiftyFour = 54,
  sixty = 60,
  sixtySix = 66,
  seventyTwo = 72,
  seventyEight = 78,
  eightyFour = 84,
}

export enum EMask {
  phoneNumberMask = '[1-]000-000-0000',
  sinMask = '000 000 000',
  postalCode = 'a0a 0a0',
}

export enum EExistingCarLoanStatus {
  ApplicantsCurrentlyHaveNoVehicles = 'applicantsCurrentlyHaveNoVehicles',
  OnlyCoApplicantAlreadyHasVehicle = 'onlyCoApplicantAlreadyHasVehicle',
  OnlyApplicantAlreadyHasVehicle = 'onlyApplicantAlreadyHasVehicle',
  ApplicantsCurrentlyHaveVehicleJointly = 'applicantsCurrentlyHaveVehicleJointly',
}

export const getEExistingCarLoanStatusList = lazyLoad(() => Object.values(EExistingCarLoanStatus))

export enum EPaymentMethodMerchants {
  PayCustomerByBankTransfer = 'alis*451',
  PayCustomerByCheque = 'alis*286',
}

export const InternalMerchants: readonly string[] = [
  EStaticMerchantIds.IFINANCE_MERCHANT_ID,
  EStaticMerchantIds.MEDICARD_MERCHANT_ID,
  EStaticMerchantIds.IFINANCE_DENTISTRY_MERCHANT_ID,
]

export const BannedBankingInformation = {
  BannedInstitutionNumbers: [],
  BannedTransitNumbers: ['67462'],
}

export const BankAccountHardDeclineRules: readonly string[] = [
  'fiveOrMoreMicrolenderDepositsInLast60Days',
  'threeOrMoreMicrolenderDepositsInLast30Days',
  'sevenOrMoreMircolenderPaymentsInLast30DaysWithOneNsfInLast30Days',
  'eightOrMoreLoanDepositsInLast60Days',
]

export function isHardDeclineCreditApplication(creditApplication: CreditApplication | null | undefined): boolean {
  const codes = creditApplication?.normsCreditDecision?.refusalReasonCodes ?? []
  return codes.some((code) => BankAccountHardDeclineRules.includes(code))
}

export const DocumentCategoryFlagsToUse = ['Documents']

export const DocumentFlagsToShowReduce = ['suspected_false_docs', 'falsified_docs', 'signature_mismatch', 'age_photo_mismatch', 'fake_id']

export const DocumentFlagsToShowNormal = [...DocumentFlagsToShowReduce, 'multiple_provinces_docs', 'different_dob_ids', 'simple_paystub']

export function DocumentFlagsToShow(financingProgramId: EFinancingProgram): string[] {
  switch (financingProgramId) {
    case EFinancingProgram.Products:
    case EFinancingProgram.MechanicalWarranty:
      return DocumentFlagsToShowReduce
    default:
      return DocumentFlagsToShowNormal
  }
}

export interface FinancingProgramConfig {
  maxFirstPaymentDelayInDays: number
  useSoftHit: boolean
  useConsigno: boolean
  useVouched: boolean
  requestedLoanAmountIsRequired: boolean
  useIFiScore: boolean
  useIncomeSection?: boolean
  useExpenseSection?: boolean
  useSuggestedLoan?: boolean
  useCreditDecisionFields?: boolean
  useCreditDecisionDialogFields?: boolean
  useCreateAlisLoan: boolean
  usePrivateMessage: boolean
  shouldSeeAmounts: boolean
  useCreditSection?: boolean
  noIncomeValidation: boolean
  supportsHighEstimate: boolean
  supportsUnavailableJobType: boolean
  validateMaxPaymentAmountInWorksheet: boolean
  checkAvailableCredit: boolean
  checkActivationWithin90Days: boolean
  lienAmountFee: number
  displayWorksheetWarningsAtApplicationLevel: boolean
  minInterestRate: number
  maxInterestRate: number
  supportsSinglePayment: boolean
  supportsInsurance: boolean
  useAgentLeeway: boolean
  useFlinksForBankAccountInfo: boolean
  readonly supportedTerms: ETermMax[]
  readonly supportedPaymentFrequencies: EPaymentFrequency[]
  readonly supportsCoApplicant: boolean
  maxLoanAmount?: number
  maxLoanAmountMedicard?: number
  minLoanAmount?: number
}

// TODO: refactor
export const defaultProgramConfig: FinancingProgramConfig = {
  minInterestRate: 9.99,
  maxInterestRate: 29.99,
  maxFirstPaymentDelayInDays: 30,
  useSoftHit: false,
  useConsigno: false,
  useVouched: false,
  requestedLoanAmountIsRequired: false,
  useIFiScore: true,
  useIncomeSection: true,
  useExpenseSection: true,
  useSuggestedLoan: true,
  useCreditSection: true,
  useCreditDecisionFields: true,
  useCreditDecisionDialogFields: true,
  useCreateAlisLoan: false,
  usePrivateMessage: true,
  supportsCoApplicant: true,
  noIncomeValidation: false,
  shouldSeeAmounts: false,
  supportsUnavailableJobType: false,
  supportsHighEstimate: false,
  validateMaxPaymentAmountInWorksheet: true,
  checkAvailableCredit: false,
  checkActivationWithin90Days: false,
  lienAmountFee: 0.0,
  displayWorksheetWarningsAtApplicationLevel: false,
  supportsSinglePayment: false,
  supportsInsurance: false,
  useAgentLeeway: false,
  supportedTerms: [6, 12, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72, 84],
  supportedPaymentFrequencies: [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly],
  useFlinksForBankAccountInfo: false,
}
export const Constants = {
  GetMinIncomeForProgram: (program: EFinancingProgram, isSinglePayment = false) => {
    if (EFinancingProgram.Products === program || isSinglePayment) return 0
    return 21000
  },
  // Pour le moment, le program n'est pas utilisé, le if est présent pour enlever l'erreur
  GetMinCombinedIncomeForProgram: (program: EFinancingProgram, isSinglePayment = false) => {
    if (EFinancingProgram.Products === program || isSinglePayment) return 0
    return 35000
  },
  SupportedDocumentTypes: '.jpg, .jpeg, .png, .pdf, .heic, .heif, .webp',
  SystemUserId: 'system',
  MinimumProductsLoanAmount: 750,
  UnavailableJobTypeId: '6',
  financingProgramConfigs: {
    [EFinancingProgram.Auto]: {
      ...defaultProgramConfig,
    },
    [EFinancingProgram.B2c]: {
      ...defaultProgramConfig,
      maxFirstPaymentDelayInDays: 45,
      useSoftHit: true,
      useConsigno: true,
      useVouched: true,
      requestedLoanAmountIsRequired: true,
      useCreateAlisLoan: true,
      shouldSeeAmounts: true,
      supportedPaymentFrequencies: [EPaymentFrequency.Monthly],
      supportsCoApplicant: false,
      supportsInsurance: true,
      useAgentLeeway: true,
    },
    [EFinancingProgram.AutoRsla]: {
      ...defaultProgramConfig,
      useCreditSection: false,
      useIncomeSection: false,
      useExpenseSection: false,
      supportsCoApplicant: false,
      requestedLoanAmountIsRequired: true,
      noIncomeValidation: true,
      supportsUnavailableJobType: true,
      lienAmountFee: 99.95,
      displayWorksheetWarningsAtApplicationLevel: true,
      useAgentLeeway: true,
    },
    [EFinancingProgram.Personal]: {
      ...defaultProgramConfig,
      supportedPaymentFrequencies: [EPaymentFrequency.Monthly],
      maxFirstPaymentDelayInDays: 45,
      useCreateAlisLoan: true,
      useSoftHit: true,
      useConsigno: true,
      requestedLoanAmountIsRequired: true,
      shouldSeeAmounts: true,
      supportsHighEstimate: true,
      maxLoanAmount: 40000,
      maxLoanAmountMedicard: 50000,
      supportsInsurance: true,
      useAgentLeeway: true,
    },
    [EFinancingProgram.IfXpress]: {
      ...defaultProgramConfig,
      requestedLoanAmountIsRequired: true,
      useSoftHit: true,
      useConsigno: true,
      shouldSeeAmounts: true,
      supportedPaymentFrequencies: [EPaymentFrequency.Monthly],
      supportsInsurance: true,
    },
    [EFinancingProgram.Products]: {
      ...defaultProgramConfig,
      requestedLoanAmountIsRequired: false,
      useIFiScore: false,
      useSuggestedLoan: false,
      useCreditDecisionFields: false,
      useCreditDecisionDialogFields: false,
      noIncomeValidation: true,
      useConsigno: true,
      supportedTerms: defaultProgramConfig.supportedTerms.filter(
        (term) => term > ETermMax.six && term <= ETermMax.seventyTwo,
      ),
      supportsInsurance: true,
      useAgentLeeway: true,
    },
    [EFinancingProgram.MechanicalWarranty]: {
      ...defaultProgramConfig,
      minInterestRate: 6.99,
      maxInterestRate: 8.99,
      requestedLoanAmountIsRequired: false,
      useIFiScore: false,
      useSuggestedLoan: false,
      useCreditDecisionFields: false,
      useCreditDecisionDialogFields: false,
      noIncomeValidation: true,
      useConsigno: true,
      minLoanAmount: 750,
      supportedTerms: [ETermMax.twelve, ETermMax.twentyFour, ETermMax.thirtySix],
      supportedPaymentFrequencies: [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly, EPaymentFrequency.Single],
      supportsCoApplicant: false,
      supportsSinglePayment: true,
      useFlinksForBankAccountInfo: true,
    },
    [EFinancingProgram.CreditRsla]: {
      ...defaultProgramConfig,
      maxInterestRate: 21.99,
      useSoftHit: true,
      useCreditSection: true,
      useIncomeSection: true,
      useExpenseSection: true,
      supportsCoApplicant: false,
      requestedLoanAmountIsRequired: true,
      supportsUnavailableJobType: true,
      noIncomeValidation: true,
      lienAmountFee: 99.95,
      displayWorksheetWarningsAtApplicationLevel: true,
      validateMaxPaymentAmountInWorksheet: false,
      useAgentLeeway: true,
    },
    default: {
      ...defaultProgramConfig,
      useSuggestedLoan: false,
      useCreditDecisionFields: false,
      useCreditDecisionDialogFields: false,
    },
  } as Record<EFinancingProgram | 'default', FinancingProgramConfig>,
}

// To disable it just set the env var to an empty string
export const useDevTools = getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_DEV_TOOLS)
export const isProd = import.meta.env.VITE_ENV === 'prod'
